import { arrayConcatUnique } from '../../../core/util/array-concat-unique';
import { arrayPushUnique } from '../../../core/util/array-push-unique';
import { isInArray } from '../../../core/util/is-in-array';
import { FileItemConfig } from '../../../vas/business-objects/file/file-config.model';
import { File } from '../../../vas/business-objects/file/file.model';
import * as actions from './file-manager.actions';

export interface State {
    // an array of File for a job
    files: Array<File>;
    // the filter
    filter: {
        searchTerm: string,
        groups: Array<string>
    };
    // an array of file ids that are selected
    selectedIds: Array<string>;
    // an array of file ids that have been added or have been changed. This is used by the FormHelperService
    newOrChangedIds: Array<string>;
    // an array of File that have been removed. This is used by the FormHelperService
    removedFiles: Array<File>;
}

export const initialState: State = {
    files: [],
    filter: {
        searchTerm: '',
        groups: []
    },
    selectedIds: [],
    newOrChangedIds: [],
    removedFiles: []
};

const resequenceFiles = (files: Array<File>): {newOrChangedIds: Array<string>, files: Array<File>} => {
    const newOrChangedIds = [];
    let sequence = 0;
    const newFiles = files.map(file => {
        const newFile = new File({
            ...file,
            sequence
        });
        sequence++;
        newOrChangedIds.push(file.id);
        return newFile;
    });

    return {
        files: newFiles,
        newOrChangedIds
    };
};

// tslint:disable-next-line:only-arrow-functions cyclomatic-complexity
export function reducer(state = initialState, action: actions.Actions): State {

    switch (action.type) {
        case actions.INIT_SUCCESS: {
            return {
                ...state,
                files: [...action.payload.files].sort((a, b) => a.sequence - b.sequence)
            };
        }
        case actions.CREATE_FROM_BLOBS_SUCCESS:
        case actions.ADD_FILES: {

            const result = resequenceFiles([...state.files, ...action.payload.files]);

            return {
                ...state,
                files: result.files,
                newOrChangedIds: arrayConcatUnique(state.newOrChangedIds, result.newOrChangedIds)
            };
        }
        case actions.UPDATE: {
            const pFile = action.payload.file;
            return {
                ...state,
                files: state.files.map(file => file.id === pFile.id ? pFile : file),
                newOrChangedIds: arrayPushUnique(state.newOrChangedIds, pFile.id)
            };
        }
        case actions.REMOVE: {
            const pFile = action.payload.file;
            return {
                ...state,
                files: state.files.filter(file => file.id !== pFile.id),
                removedFiles: [...state.removedFiles, pFile],
                newOrChangedIds: state.newOrChangedIds.filter(id => id !== pFile.id)
            };
        }
        case actions.REMOVE_SELECTED: {
            const ids = state.selectedIds;

            if (!ids.length) {
                return state;
            }

            return {
                ...state,
                files: state.files.filter(file => !isInArray(file.id, ids)),
                newOrChangedIds: state.newOrChangedIds.filter(id => !isInArray(id, ids)),
                removedFiles: [...state.removedFiles, ...state.files.filter(file => isInArray(file.id, ids))],
                selectedIds: state.selectedIds.filter(id => !isInArray(id, ids))
            };
        }
        case actions.REORDER: {
            const from = action.payload.from;
            const to = action.payload.to;
            const files = [...state.files];

            if (from < 0 || from > files.length - 1) {
                return state;
            } else if (to < 0 || to > files.length - 1) {
                return state;
            }

            const element = files[from];
            files.splice(from, 1);
            files.splice(to, 0, element);

            const result = resequenceFiles(files);

            return {
                ...state,
                files: result.files,
                newOrChangedIds: arrayConcatUnique(state.newOrChangedIds, result.newOrChangedIds)
            };
        }
        case actions.RESET: {
            return {...initialState};
        }
        case actions.ROTATE_RIGHT: {

            let config = new FileItemConfig(action.payload.file.config);
            const currentRotation = config.rotation || 0;
            config = config.rotate(currentRotation + 90);

            return {
                ...state,
                files: state.files
                    .map(file => {
                        if (file.id !== action.payload.file.id) {
                            return file;
                        }
                        return new File({
                            ...action.payload.file,
                            config
                        });
                    }),
                newOrChangedIds: arrayPushUnique(
                    state.newOrChangedIds,
                    action.payload.file.id
                )
            };
        }
        case actions.SELECT_ALL: {
            return {
                ...state,
                selectedIds: state.files.map(file => file.id)
            };
        }
        case actions.SELECT_NONE: {
            return {
                ...state,
                selectedIds: []
            };
        }
        case actions.SET_SELECTED: {
            return {
                ...state,
                selectedIds: action.payload.selected ?
                    arrayPushUnique(state.selectedIds, action.payload.file.id) :
                    state.selectedIds.filter(id => id !== action.payload.file.id)
            };
        }
        case actions.SET_GROUP_FOR_SELECTED: {
            const ids = state.selectedIds;

            return {
                ...state,
                files: state.files.map(file => {

                    if (!isInArray(file.id, ids)) {
                        return file;
                    }

                    return new File({
                        ...file,
                        config: new FileItemConfig({
                            ...file.config,
                            group: action.payload.group
                        })
                    });
                }),
                newOrChangedIds: arrayConcatUnique(state.newOrChangedIds, ids)
            };
        }
        case actions.SET_FILTER_GROUP: {
            return {
                ...state,
                filter: {
                    ...state.filter,
                    groups: action.payload.groups
                }
            };
        }
        case actions.SET_FILTER_SEARCH_TERM: {
            return {
                ...state,
                filter: {
                    ...state.filter,
                    searchTerm: action.payload.searchTerm
                }
            };
        }
        case actions.TOGGLE_DISPLAY_AS_ACTION: {
            const file = new File({
                ...action.payload.file,
                config: {
                    ...action.payload.file.config,
                    displayAs: action.payload.file.config.displayAs === 'full' ?
                        'grid' :
                        'full'
                }
            });

            return {
                ...state,
                files: state.files.map(f => f.id === file.id ? file : f),
                newOrChangedIds:  arrayPushUnique(
                    state.newOrChangedIds,
                    action.payload.file.id
                )
            };
        }
        default: {
            return state;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/molecules/file-manager/file-manager.reducer.ts